// src/utils/requestQueue.js
// Request queue with automatic retry for handling transient network failures.
//
// TAS-188: entries may optionally carry a SERIALIZABLE `persist` descriptor
// ({ ops: [{ table, row, onConflict }] }). Those entries are mirrored to
// localStorage and REPLAYED on the next page load, so a submitted answer/rating
// survives a reload/crash before the queue drained. `execute` closures are not
// serializable, so only entries with a `persist` descriptor are durable —
// everything else stays RAM-only exactly as before. The replayed write MUST be
// idempotent (upsert on a unique key — migration 054) so replay can't duplicate.

import { supabase } from '@tasted/utils/supabase';
import { claimTokenStorage } from './playerStorage';
import { legacyWineReviewOpToRpc } from './wineReview';
import * as Sentry from '@sentry/react';

const __DEV__ = import.meta.env.DEV;
const debugLog = __DEV__ ? console.log.bind(console, '[RequestQueue]') : () => {};
const debugWarn = __DEV__ ? console.warn.bind(console, '[RequestQueue]') : () => {};

const STORAGE_KEY = 'tasted:requestQueue:v1';
// Longest the `online` handler waits on the resume gate before resuming anyway.
const RESUME_GATE_MAX_MS = 4000;

// Round-2 review (TAS-412): the parked-entry sweep below was unbounded in
// time — a durable write for a room everyone has long since moved on from
// some other way (a reset, a reload that resolved it a different way) would
// keep getting retried forever. Past this age a parked entry is dropped
// instead, with a Sentry capture so it's visible rather than silently gone.
const MAX_PARKED_AGE_MS = 30 * 60 * 1000; // 30 minutes

// Part 2 (spec §2 error codes, M1): SQLSTATEs a retry can never fix. The entry
// is removed and its onError runs straight away, and the terminal handler (the
// provider re-reads session_live_state) is told. P0429 (rate limit) is NOT here:
// it stays retryable with backoff.
//   42501 ownership / role          22023 malformed input
//   P0403 wrong session status      P0410 stale run or attempt ("answer again")
//   P0412 precondition              P0423 budget used up
//   P0501 feature switched off for this session
export const TERMINAL_ERROR_CODES = Object.freeze([
  '42501', '22023', 'P0403', 'P0410', 'P0412', 'P0423', 'P0501',
]);

export function isTerminalError(error) {
  return TERMINAL_ERROR_CODES.includes(error?.code);
}

// A9 (edge-case audit 2026-09-28): a 42501 "not your player" means the seat's
// claim token was rotated (a readmit moved the seat, or this phone lost it).
// Every other 42501 — "answer is final", "this round is not accepting
// answers", "only the host can advance" — is a real refusal.
export function isSeatTokenRefused(error) {
  return error?.code === '42501' && /not your player/i.test(String(error?.message || ''));
}

// Must match playerStorage's CLAIM_TOKEN_CHANGED_EVENT (a literal here so a
// test that mocks playerStorage doesn't have to know about it).
const CLAIM_TOKEN_CHANGED_EVENT = 'tasted:claim-token-changed';

// How long a durable write refused for its token waits for this phone to get
// its seat back before it is dropped like any other terminal failure.
export const TOKEN_WAIT_MS = 30 * 60 * 1000;

const TOKEN_RPCS = {
  submit_answer_v2: ['p_user_id', 'p_token'],
  submit_game_answer: ['p_user_id', 'p_token'],
  submit_round_answer: ['p_user_id', 'p_token'],
  append_game_event: ['p_player_id', 'p_claim_token'],
  append_game_event_secure: ['p_player_id', 'p_claim_token'],
};

// A9: the writes that wait for a readmit — the player's answers, which the
// server dedupes per (round, player) and refuses once the round has closed.
const PARKABLE_RPCS = new Set(['submit_answer_v2', 'submit_game_answer', 'submit_round_answer']);

/**
 * The seat a durable entry writes as, and the token it would send right now
 * (the executor sends the stored token for that player, falling back to the
 * one frozen at enqueue time). null for entries that don't carry a token.
 */
export function entrySeat(item) {
  const ops = item?.persist?.ops;
  if (!Array.isArray(ops)) return null;
  for (const op of ops) {
    const keys = op?.rpc ? TOKEN_RPCS[op.rpc] : null;
    if (!keys) continue;
    const playerId = op.args?.[keys[0]];
    if (!playerId) continue;
    return { playerId, token: claimTokenStorage.get(playerId) || op.args?.[keys[1]] || null };
  }
  return null;
}

/**
 * What an entry belongs to: `{ sessionCode, runId, roundId, attempt }` (any may
 * be null). An explicit `tag` passed to enqueue wins; otherwise it is read off
 * the persist descriptor, so a durable entry keeps its tag across a reload.
 * submit_round_answer carries run + attempt in its own args; a Part 2 gm-next
 * append carries `runId` in its payload; table upserts carry `session_id`.
 */
export function entryTag(item) {
  if (!item) return null;
  if (item.tag && typeof item.tag === 'object') return item.tag;
  const ops = item.persist?.ops;
  if (!Array.isArray(ops)) return null;
  for (const op of ops) {
    if (op?.rpc) {
      const a = op.args || {};
      if (a.p_session_id) {
        return {
          sessionCode: a.p_session_id,
          runId: a.p_run_id ?? a.p_payload?.runId ?? null,
          roundId: a.p_round_id ?? null,
          attempt: Number.isInteger(a.p_attempt) ? a.p_attempt : null,
        };
      }
    } else if (op?.row?.session_id) {
      return { sessionCode: op.row.session_id, runId: null, roundId: op.row.round_id ?? null, attempt: null };
    }
  }
  return null;
}

// Runs a persist descriptor. Injectable via setPersistExecutor() (tests swap in a
// fake). The default performs the ops against supabase, throwing on error so the
// queue's retry/backoff kicks in. Three op shapes:
//   { table, row, onConflict }               → plain upsert (answer/score/review writes)
//   { table, row, insertIgnoreConflict }     → insert where a 23505 unique-violation
//                                              counts as SUCCESS (TAS-253 gm-next: its
//                                              dedupe index is an expression index,
//                                              which PostgREST's on_conflict can't
//                                              target — another writer having already
//                                              persisted the row IS the desired state)
//   { rpc, args }                            → supabase.rpc call (TAS-162 server-side
//                                              scoring — the server recomputes points
//                                              on replay, so a stale frozen score can
//                                              never overwrite a fresh one)
async function defaultPersistExecutor(persist) {
  const ops = persist?.ops || [];
  for (const rawOp of ops) {
    // Audit G1: a rating queued by an older client as a direct table upsert is
    // replayed through submit_wine_review (227 revokes the direct write).
    const op = legacyWineReviewOpToRpc(rawOp) || rawOp;
    if (op.rpc) {
      let args = op.args || {};
      // TAS-360: a queued append_game_event carries the claim token from
      // enqueue time. A rejoin rotates the token, so a replay after that would
      // send a dead one; resolve the current token for the same player now.
      const rpc = op.rpc === 'append_game_event' ? 'append_game_event_secure' : op.rpc;
      if (rpc === 'append_game_event_secure' && args.p_player_id) {
        const fresh = claimTokenStorage.get(args.p_player_id);
        if (fresh) args = { ...args, p_claim_token: fresh };
      }
      if (['submit_answer_v2', 'submit_game_answer', 'submit_round_answer'].includes(rpc) && args.p_user_id) {
        const fresh = claimTokenStorage.get(args.p_user_id);
        if (fresh) args = { ...args, p_token: fresh };
      }
      if (rpc === 'submit_wine_review' && args.p_player_id) {
        const fresh = claimTokenStorage.get(args.p_player_id);
        if (fresh) args = { ...args, p_claim_token: fresh };
      }
      const res = await supabase.rpc(rpc, args);
      if (res?.error) throw res.error;
      continue;
    }
    if (op.insertIgnoreConflict) {
      const res = await supabase.from(op.table).insert(op.row);
      if (res?.error && res.error.code !== '23505') throw res.error;
      continue;
    }
    const res = await supabase
      .from(op.table)
      .upsert(op.row, op.onConflict ? { onConflict: op.onConflict } : undefined);
    if (res?.error) throw res.error;
  }
}

// Part 2 (review finding 2): how long a rehydrated run-tagged entry waits
// for the provider's stale-entry check before the queue checks the live run
// itself.
export const REHYDRATE_HOLD_MS = 8000;

/** The room's current run id, or undefined when it can't be read. */
async function defaultLiveRunReader(sessionCode) {
  try {
    const { data, error } = await supabase
      .from('session_live_state')
      .select('run_id')
      .eq('session_id', sessionCode)
      .maybeSingle();
    if (error) return undefined;
    return data?.run_id ?? null;
  } catch {
    return undefined;
  }
}

class RequestQueue {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second
    this.maxDelay = 30000; // 30 seconds
    this.listeners = new Set();
    this.persistExecutor = defaultPersistExecutor;
    // Part 2 hooks, each owned by one registrant at a time (the mounted
    // GameFlowProvider). Setters return an unregister that only clears its own
    // registration, so an unmounting provider can't wipe a newer one's.
    this.terminalHandler = null;   // (error, entry) => void, after a terminal error
    this.entryValidator = null;    // (entry) => boolean; false drops the entry unsent
    this.resumeGate = null;        // async () => void, awaited on `online` before resuming
    this.liveRunReader = defaultLiveRunReader;
    this._holdTimer = null;
  }

  /** Tests inject a fake `(sessionCode) => Promise<runId|null|undefined>`. */
  setLiveRunReader(fn) {
    this.liveRunReader = typeof fn === 'function' ? fn : defaultLiveRunReader;
  }

  /**
   * Release entries rehydrated with a run tag. With a validator registered
   * they go through it as usual. Without one (the provider hasn't mounted, or
   * its flags haven't loaded), the queue reads each session's live run and
   * drops entries from a run the room has left; an unreadable run keeps them,
   * since a player's data is never thrown away on a guess.
   */
  async _releaseHeld() {
    clearTimeout(this._holdTimer);
    this._holdTimer = null;
    const held = this.queue.filter((item) => item.held || this._isWaiting(item));
    if (held.length === 0) return;
    {
      const runs = new Map();
      for (const item of held) {
        const code = entryTag(item)?.sessionCode;
        if (code && !runs.has(code)) runs.set(code, await this.liveRunReader(code));
      }
      for (const item of held) {
        const tag = entryTag(item);
        const live = tag?.sessionCode ? runs.get(tag.sessionCode) : undefined;
        if (typeof live === 'string' && live && tag.runId && live !== tag.runId) {
          debugWarn('Dropping rehydrated entry from an old run:', item.id);
          item.held = false;
          const stale = Object.assign(new Error('stale run'), { code: 'P0410', stale: true });
          this._dropTerminal(item, stale);
        } else if (typeof live === 'string' && live) {
          item.runChecked = true;
        }
      }
    }
    for (const item of this.queue) item.held = false;
    this.processQueue();
  }

  _setHook(name, fn) {
    const value = typeof fn === 'function' ? fn : null;
    this[name] = value;
    return () => { if (this[name] === value) this[name] = null; };
  }

  /** Called with (error, entry) whenever an entry ends on a terminal error code. */
  setTerminalErrorHandler(fn) { return this._setHook('terminalHandler', fn); }

  /**
   * Called with the entry just before it is sent. Returning `false` drops it
   * (removed, persisted state updated, onError called with a P0410-shaped
   * error). Used to drop answers and ratings from an old run or attempt.
   */
  setEntryValidator(fn) {
    const off = this._setHook('entryValidator', fn);
    // Entries held since a reload can now be checked the normal way.
    if (this.entryValidator && this.queue.some((item) => item.held)) {
      // The fallback timer stays armed: if the validator can only answer
      // 'wait' (no run known yet), the timer's live-run check decides.
      for (const item of this.queue) item.held = false;
      this.processQueue();
    }
    return off;
  }

  /** Awaited (bounded) on `online` before parked entries are re-armed. */
  setResumeGate(fn) { return this._setHook('resumeGate', fn); }

  _isStale(item) {
    if (!this.entryValidator) return false;
    try {
      return this.entryValidator(item) === false;
    } catch {
      return false; // a validator bug must never eat player data
    }
  }

  /**
   * The validator can't decide yet ('wait': the provider doesn't know the
   * room's run). Such an entry is skipped until it can, or until the hold
   * fallback has checked its run against the live row itself.
   */
  _isWaiting(item) {
    if (!this.entryValidator || item.runChecked) return false;
    try {
      return this.entryValidator(item) === 'wait';
    } catch {
      return false;
    }
  }

  /**
   * A9: a durable answer refused because this phone's token no longer
   * holds the seat is kept (in RAM and storage) instead of dropped, and sent
   * again once the stored token for that player changes — a readmit or a
   * rejoin. The server is idempotent on replay: the same answer returns its
   * original receipt, and an answer for a round that has since closed comes
   * back as a real 42501 and is dropped then.
   */
  _parkForToken(item, error, seat) {
    if (!item.persist || item.disposable || !isSeatTokenRefused(error)) return false;
    // Only the player's own answers. A gm-next or other game event replayed
    // minutes later could move the room (review of A9), so those stay terminal.
    if (!item.persist.ops?.every((op) => PARKABLE_RPCS.has(op?.rpc))) return false;
    if (!seat?.token) return false;
    const since = Number.isFinite(item.tokenWaitSince) ? item.tokenWaitSince : Date.now();
    if (Date.now() - since >= TOKEN_WAIT_MS) return false;
    item.awaitingToken = { playerId: seat.playerId, token: seat.token, error };
    item.tokenWaitSince = since;
    item.retries = 0;
    this._savePersisted();
    debugWarn('Parked until this phone gets its seat back:', item.id);
    this.notifyListeners();
    return true;
  }

  /** Still waiting: the stored token is the one the server refused. */
  _isAwaitingToken(item) {
    const wait = item.awaitingToken;
    if (!wait) return false;
    const current = claimTokenStorage.get(wait.playerId);
    if (current && current !== wait.token) {
      item.awaitingToken = null; // readmitted: send it with the new token
      return false;
    }
    return true;
  }

  /** Drop entries that waited TOKEN_WAIT_MS for a token that never came. */
  _expireTokenWaits(now = Date.now()) {
    const expired = this.queue.filter(
      (item) => item.awaitingToken && now - item.tokenWaitSince >= TOKEN_WAIT_MS
    );
    for (const item of expired) {
      const { error } = item.awaitingToken;
      item.awaitingToken = null;
      this._dropTerminal(item, error);
    }
    return expired.length;
  }

  _dropTerminal(item, error) {
    const idx = this.queue.indexOf(item);
    if (idx !== -1) this.queue.splice(idx, 1);
    if (item.persist) this._savePersisted();
    try { item.onError?.(error); } catch { /* observer failure */ }
    try { this.terminalHandler?.(error, item); } catch { /* observer failure */ }
    this.notifyListeners();
  }

  /**
   * Remove every entry that belongs to `sessionCode` except those tagged with
   * `keepRunId` (spec §5 Reset: an in-game reset purges the old run's answers,
   * ratings and events; identity is kept). Entries for other sessions and
   * entries with no session are untouched. Returns the number removed.
   */
  purgeSession(sessionCode, { keepRunId = null } = {}) {
    if (!sessionCode) return 0;
    const doomed = this.queue.filter((item) => {
      const tag = entryTag(item);
      if (!tag || tag.sessionCode !== sessionCode) return false;
      return !(keepRunId && tag.runId === keepRunId);
    });
    if (doomed.length === 0) return 0;
    this.queue = this.queue.filter((item) => !doomed.includes(item));
    if (doomed.some((item) => item.persist)) this._savePersisted();
    debugLog('Purged', doomed.length, 'entr(ies) for session', sessionCode);
    this.notifyListeners();
    return doomed.length;
  }

  /**
   * Add a request to the queue
   * @param {Object} options
   * @param {string} options.id - Unique identifier for deduplication
   * @param {Function} [options.execute] - Async function to execute. Optional when
   *   `persist` is given (then the persist executor is used).
   * @param {Object} [options.persist] - Serializable descriptor ({ ops: [...] }) that
   *   makes this entry survive a reload. Replayed via the persist executor.
   * @param {Function} options.onSuccess - Called on success
   * @param {Function} options.onError - Called after all retries fail
   * @param {number} options.priority - Higher = processed first (default: 0)
   * @param {Object} [options.tag] - `{ sessionCode, runId, roundId, attempt }` for
   *   entries with no persist descriptor to read it from (ratings, RAM-only
   *   writes). Lets purgeSession() and the stale-entry validator find them.
   */
  /**
   * `disposable`: the entry may be DROPPED once parked past MAX_PARKED_AGE_MS
   * (a game-start event for a room that has long since moved on). Default is
   * false: an answer or a wine rating is the player's data and is never
   * thrown away by age — it waits for connectivity, however long.
   * `addedAt` is only passed by rehydrate(), so a reload does not restart
   * the age clock.
   */
  enqueue({ id, execute, persist, onSuccess, onError, priority = 0, disposable = false, addedAt, tag, held = false, tokenWaitSince }) {
    // Deduplicate by ID - if same request is already queued, skip
    const existingIndex = this.queue.findIndex(item => item.id === id);
    if (existingIndex !== -1) {
      // Re-review finding 3: a held (rehydrated, unchecked) entry, or one
      // from another run or attempt, is older data than this one. Replace it,
      // or the new answer/rating would be dropped along with it.
      const existing = this.queue[existingIndex];
      const oldTag = entryTag(existing);
      const newTag = entryTag({ tag, persist });
      const differs = Boolean(oldTag && newTag) &&
        ((oldTag.runId ?? null) !== (newTag.runId ?? null) || (oldTag.attempt ?? null) !== (newTag.attempt ?? null));
      if (!existing.held && !differs) {
        debugLog('Request already queued, skipping:', id);
        return;
      }
      debugLog('Replacing an older queued entry with the same id:', id);
      this.queue.splice(existingIndex, 1);
      if (existing.persist && !persist) this._savePersisted();
    }

    // A persist-only entry (no closure) runs its descriptor via the executor —
    // this is also the path a rehydrated entry takes after a reload.
    const run = execute || (persist ? () => this.persistExecutor(persist) : null);
    if (!run) {
      debugWarn('enqueue called with neither execute nor persist:', id);
      return;
    }

    this.queue.push({
      id,
      execute: run,
      persist: persist || null,
      onSuccess,
      onError,
      priority,
      retries: 0,
      disposable: Boolean(disposable),
      addedAt: Number.isFinite(addedAt) ? addedAt : Date.now(),
      tag: tag && typeof tag === 'object' ? tag : null,
      // Rehydrated with a run tag: not sent until checked (see _releaseHeld).
      held: Boolean(held),
      // A9: when a token refusal first parked this entry (survives a reload).
      tokenWaitSince: Number.isFinite(tokenWaitSince) ? tokenWaitSince : undefined,
    });

    // Sort by priority (higher first)
    this.queue.sort((a, b) => b.priority - a.priority);

    if (persist) this._savePersisted();
    debugLog('Request queued:', id, 'Queue size:', this.queue.length);
    this.notifyListeners();
    this.processQueue();
  }

  async processQueue() {
    if (this.isProcessing) return;

    // Check if online before processing
    if (!navigator.onLine) {
      debugLog('Offline - waiting for connection...');
      return;
    }

    this.isProcessing = true;

    try {
      while (navigator.onLine) {
        // Pick the first entry that isn't parked (a durable entry that already
        // exhausted its retries — see below). `find` keeps priority order.
        const item = this.queue.find(
          it => !it.deferred && !it.held && !this._isWaiting(it) && !this._isAwaitingToken(it)
        );
        if (!item) break;

        // Part 2 (L1): an answer or rating from a run or attempt the room has
        // left is dropped before it is sent, not replayed into the new one.
        if (this._isStale(item)) {
          debugWarn('Dropping stale entry (old run or attempt):', item.id);
          const stale = Object.assign(new Error('stale run or attempt'), { code: 'P0410', stale: true });
          this._dropTerminal(item, stale);
          continue;
        }

        let sentSeat = null;
        try {
          debugLog('Processing request:', item.id, 'Attempt:', item.retries + 1);
          // A9: the token this attempt sends, read before the await — a
          // readmit can store a new one while the request is in flight.
          sentSeat = item.persist ? entrySeat(item) : null;
          await item.execute();

          // Remove by IDENTITY, not shift(): the queue may have been mutated
          // during the await (e.g. remove()/enqueue), so shift() could drop the
          // wrong request. If it's already gone, it was cancelled mid-flight —
          // skip its success callback.
          const idx = this.queue.indexOf(item);
          if (idx === -1) continue;
          this.queue.splice(idx, 1);
          if (item.persist) this._savePersisted();
          debugLog('Request succeeded:', item.id);

          if (item.onSuccess) {
            try {
              item.onSuccess();
            } catch (e) {
              debugWarn('onSuccess callback error:', e);
            }
          }

          this.notifyListeners();
        } catch (error) {
          // If it was removed mid-flight, abandon quietly — don't retry a
          // request the caller already cancelled.
          const idx = this.queue.indexOf(item);
          if (idx === -1) continue;

          debugWarn('Request failed:', item.id, error.message);
          if (isTerminalError(error)) {
            if (this._parkForToken(item, error, sentSeat)) continue;
            this._dropTerminal(item, error);
            continue;
          }
          item.retries++;

          if (item.retries >= this.maxRetries) {
            if (item.persist) {
              // DURABLE entries must NOT be discarded on max retries — that would
              // defeat "survives reload/replay". Park it (kept in RAM + storage,
              // skipped by the loop so it can't hot-spin) and re-arm it on the
              // next reconnect (online handler) or reload (rehydrate).
              item.deferred = true;
              item.retries = 0;
              debugWarn('Durable request parked after max retries (retries on reconnect/reload):', item.id);
              this.notifyListeners();
            } else {
              // RAM-only: give up and roll back.
              this.queue.splice(idx, 1);
              debugWarn('Max retries reached for:', item.id);
              if (item.onError) {
                try {
                  item.onError(error);
                } catch (e) {
                  debugWarn('onError callback error:', e);
                }
              }
              this.notifyListeners();
            }
          } else {
            // Wait with exponential backoff before retrying
            const delay = Math.min(
              this.baseDelay * Math.pow(2, item.retries - 1),
              this.maxDelay
            );
            debugLog('Retrying in', delay, 'ms');
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove a specific request from the queue
   */
  remove(id) {
    const index = this.queue.findIndex(item => item.id === id);
    if (index !== -1) {
      const [removed] = this.queue.splice(index, 1);
      if (removed?.persist) this._savePersisted();
      debugLog('Request removed:', id);
      this.notifyListeners();
    }
  }

  /**
   * Clear all pending requests
   */
  clear() {
    clearTimeout(this._holdTimer);
    this._holdTimer = null;
    this.queue = [];
    this._savePersisted();
    debugLog('Queue cleared');
    this.notifyListeners();
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      pending: this.queue.length,
      isProcessing: this.isProcessing,
      items: this.queue.map(item => ({
        id: item.id,
        retries: item.retries,
        addedAt: item.addedAt,
      })),
    };
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (e) {
        debugWarn('Listener error:', e);
      }
    });
  }

  // ── Durable persistence (TAS-188) ──────────────────────────────────────────

  /** Swap the persist executor (tests inject a fake). */
  setPersistExecutor(fn) {
    this.persistExecutor = typeof fn === 'function' ? fn : defaultPersistExecutor;
  }

  /** Mirror the persistable entries (those with a `persist` descriptor) to storage. */
  _savePersisted() {
    if (typeof window === 'undefined') return;
    try {
      const snapshot = this.queue
        .filter(item => item.persist)
        .map(item => ({
          id: item.id,
          persist: item.persist,
          priority: item.priority,
          addedAt: item.addedAt,
          disposable: item.disposable,
          tag: item.tag || undefined,
          tokenWaitSince: Number.isFinite(item.tokenWaitSince) ? item.tokenWaitSince : undefined,
        }));
      if (snapshot.length === 0) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      }
    } catch (e) {
      debugWarn('Failed to persist queue:', e?.message);
    }
  }

  _readPersisted() {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      debugWarn('Failed to read persisted queue:', e?.message);
      return [];
    }
  }

  /**
   * Re-enqueue any persisted entries from a previous session and process them.
   * Idempotent: dedup-by-id means calling it twice (or racing a fresh enqueue of
   * the same id) won't double-run. Safe to call once the persist executor is set.
   */
  rehydrate() {
    const persisted = this._readPersisted();
    if (persisted.length === 0) return;
    debugLog('Rehydrating', persisted.length, 'persisted request(s)');
    let anyHeld = false;
    for (const entry of persisted) {
      if (!entry?.id || !entry?.persist) continue;
      // Part 2 (review finding 2): an entry that names a run waits for the
      // stale-entry check, which the provider registers only after it mounts.
      // Replaying it at module load could land an old run's rating after a
      // session reset.
      const runTagged = Boolean(entryTag({ tag: entry.tag, persist: entry.persist })?.runId);
      const held = runTagged && !this.entryValidator;
      anyHeld = anyHeld || held;
      this.enqueue({
        id: entry.id,
        persist: entry.persist,
        priority: entry.priority || 0,
        disposable: Boolean(entry.disposable),
        addedAt: entry.addedAt,
        tag: entry.tag,
        held,
        tokenWaitSince: entry.tokenWaitSince,
      });
    }
    if (anyHeld) {
      clearTimeout(this._holdTimer);
      this._holdTimer = setTimeout(() => { this._releaseHeld(); }, REHYDRATE_HOLD_MS);
    }
  }

  /** Test/utility: drop all persisted state. */
  clearPersisted() {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      debugWarn('Failed to clear persisted queue:', e?.message);
    }
  }

  /**
   * Re-arm any durable entries parked after exhausting their retries, so
   * the next processQueue() gives them a fresh attempt.
   */
  unparkAll() {
    for (const item of this.queue) {
      if (item.deferred) {
        item.deferred = false;
        item.retries = 0;
      }
    }
  }

  /**
   * Setup online/offline handlers
   */
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      debugLog('Back online - processing queue');
      // Part 2 (spec §5 Queue): let the provider re-read session_live_state
      // first, so the stale-entry check sees the room as it is now. Bounded:
      // a gate that hangs must not hold the player's answers back.
      const gate = this.resumeGate;
      const resume = () => {
        this.unparkAll();
        this.processQueue();
      };
      if (!gate) { resume(); return; }
      let timer = null;
      Promise.race([
        Promise.resolve().then(() => gate()).catch(() => {}),
        new Promise((r) => { timer = setTimeout(r, RESUME_GATE_MAX_MS); }),
      ]).finally(() => {
        clearTimeout(timer);
        resume();
      });
    });

    window.addEventListener('offline', () => {
      debugLog('Went offline - pausing queue');
    });

    // A9: a new claim token (readmit, rejoin) releases entries parked on the
    // old one.
    window.addEventListener(CLAIM_TOKEN_CHANGED_EVENT, () => {
      if (this.queue.some((item) => item.awaitingToken)) this.processQueue();
    });
  }

  /**
   * TAS-412 review: a durable entry parked after exhausting its retries
   * previously got a fresh attempt only from the `online` event or a
   * reload/rehydrate. navigator.onLine reflects whether a network
   * interface is up, not whether the backend is reachable — a venue-wifi
   * or upstream-server outage can recover while the browser's `online`/
   * `offline` events never fire at all, since the interface stayed "up"
   * throughout. Without this, a parked entry (the game-start gm-next
   * write, in Lobby.jsx's case) could sit forever with no visible way to
   * retry short of a reload. A slow, bounded periodic sweep gives it
   * another path that doesn't depend on the browser's own belief about
   * connectivity — same retry/backoff/park behaviour as any other
   * attempt, just re-armed on a timer instead of an event.
   */
  startParkedRetrySweep(intervalMs = 20000) {
    if (this._parkedSweepId) return; // idempotent — one sweep per process
    this._parkedSweepId = setInterval(() => {
      const now = Date.now();
      // Drop anything parked past MAX_PARKED_AGE_MS before re-arming what's
      // left — an unbounded retry of very old state is its own failure
      // mode, not a safety net.
      // Only DISPOSABLE entries expire (final review: this used to drop a
      // parked answer or wine rating after a 31-minute upstream outage with
      // navigator.onLine still true — unsent player data, gone from memory
      // and storage). Everything else keeps waiting for connectivity.
      const stale = this.queue.filter(
        (item) => item.deferred && item.disposable && now - item.addedAt >= MAX_PARKED_AGE_MS
      );
      for (const item of stale) {
        const idx = this.queue.indexOf(item);
        if (idx !== -1) this.queue.splice(idx, 1);
        if (item.persist) this._savePersisted();
        debugWarn('Parked entry dropped after max age:', item.id);
        Sentry.captureMessage('requestQueue: parked entry dropped after max age', {
          level: 'warning',
          tags: { operation: 'request_queue_parked_max_age' },
          extra: { id: item.id, ageMs: now - item.addedAt },
        });
        try {
          item.onError?.(new Error('Parked entry dropped after max age'));
        } catch {
          /* observer failure */
        }
      }
      if (stale.length) this.notifyListeners();

      // A9: a token that never came back — drop what waited on it; send what
      // a new token has released.
      this._expireTokenWaits(now);
      if (this.queue.some((item) => item.awaitingToken)) this.processQueue();

      if (!this.queue.some((item) => item.deferred)) return;
      debugLog('Parked-entry sweep - re-arming and retrying');
      this.unparkAll();
      this.processQueue();
    }, intervalMs);
  }

  /** Test utility: stop the sweep so a test can restart it at a faster interval. */
  stopParkedRetrySweep() {
    if (this._parkedSweepId) {
      clearInterval(this._parkedSweepId);
      this._parkedSweepId = null;
    }
  }
}

// Exported for direct unit tests of the op shapes (upsert / insertIgnoreConflict / rpc).
export { defaultPersistExecutor };

// Singleton instance
export const requestQueue = new RequestQueue();

// Setup network listeners + replay any writes persisted before a reload.
if (typeof window !== 'undefined') {
  requestQueue.setupNetworkListeners();
  requestQueue.rehydrate();
  requestQueue.startParkedRetrySweep();
}

/**
 * Helper to create a queued request with optimistic update.
 * @param {Object} options
 * @param {string} options.id - Unique request ID
 * @param {Function} [options.optimisticUpdate] - Called immediately to update UI
 * @param {Function} [options.execute] - Async function to execute (optional if `persist` given)
 * @param {Object} [options.persist] - Serializable descriptor to survive a reload (TAS-188)
 * @param {Function} [options.onSuccess] - Called once the queued write actually
 *   persists (TAS-414 reviewer follow-up: this is the hook callers should use
 *   for anything that must NOT fire before persistence is confirmed — e.g.
 *   broadcasting an answer to other players — since `queueWithOptimisticUpdate`
 *   itself returns before `execute` even starts).
 * @param {Function} [options.rollback] - Called if all retries fail
 */
export function queueWithOptimisticUpdate({ id, optimisticUpdate, execute, persist, onSuccess, rollback, tag }) {
  // Apply optimistic update immediately
  if (optimisticUpdate) {
    try {
      optimisticUpdate();
    } catch (e) {
      debugWarn('Optimistic update error:', e);
    }
  }

  // Queue the actual request
  requestQueue.enqueue({
    id,
    execute,
    persist,
    onSuccess,
    tag,
    onError: (error) => {
      debugWarn('Request failed permanently, rolling back:', id);
      if (rollback) {
        try {
          rollback(error);
        } catch (e) {
          debugWarn('Rollback error:', e);
        }
      }
    },
  });
}

export default requestQueue;
